import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import axios from "axios";
import liff from "@line/liff";
import { ensureLiffInit } from "../../liffClient";
import { colors, fontFamily } from "../../theme/palette";

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export default function AddWordModal({ open, onClose, onAdded }: Props) {
  const [word, setWord] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    const trimmed = word.trim();
    if (!trimmed) return;

    setLoading(true);
    setError("");
    try {
      await ensureLiffInit();
      const profile = await liff.getProfile();
      await axios.post("/v1/api/word/add", {
        userId: profile.userId,
        word: trimmed,
      });
      setWord("");
      onAdded();
      onClose();
    } catch {
      setError("登録に失敗しました。もう一度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setWord("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          p: 1,
          backgroundColor: colors.card,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: colors.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TranslateRoundedIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: colors.textPrimary, fontFamily }}>
            単語を追加
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="例: apple, bonjour, ..."
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          disabled={loading}
          variant="outlined"
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "&.Mui-focused fieldset": {
                borderColor: colors.accent,
              },
            },
          }}
        />
        {error && (
          <Typography sx={{ color: colors.danger, fontSize: "0.8rem", mt: 1, fontFamily }}>
            {error}
          </Typography>
        )}
        <Typography sx={{ color: colors.textMuted, fontSize: "0.78rem", mt: 1.5, fontFamily }}>
          翻訳と画像は自動で生成されます
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: colors.textSecondary,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontFamily,
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleAdd}
          disabled={loading || !word.trim()}
          variant="contained"
          sx={{
            background: colors.accent,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontFamily,
            px: 3,
            "&:hover": {
              background: colors.accentDark,
            },
            "&.Mui-disabled": {
              background: colors.border,
            },
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "追加"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
